import type { ReagentDef } from '@ctcells/contract'

import type { DeckRackKind, RackOccupancy, RackReagents } from '../../figures/DeckRackFigure.tsx'
import { REST_CELLS } from '../../figures/deckRackDefinitions.ts'

import { sameLabware } from './deckRacks.ts'

/*
 * **데크 랙 칸 상태 — 정하는 곳은 여기 한 곳이다.** 소모품 서랍(`SupplyDetail`)이 부른다(시료 탭 오른쪽 데크 판은 2026-09-28,
 * 콘솔 장비 탭(`FocusFigure` 의 `realFigures`)의 랙 그림은 2026-10-03 에 걷혔다). 그림(`DeckRackFigure`)은 받은 맵을 그릴 뿐 추론하지 않는다.
 *
 * 🔴 **모르면 «가득»이다**(eric 2026-09-27 원문: 「시료 쪽에 지금 팁랙 · 튜브랙 이런 게 다 비어 있는데,
 *    작업 시작할 때 다 채워져 있는 것으로 보여줘. … 저거 상황을 모르면 일단 다 채워져 있는 것으로 해줘.」)
 *    ⇒ 빈 맵을 돌려주고, 그림은 맵에 없는 칸을 `full` 로 그린다.
 *
 * ## 지금 출처가 없다 — 이을 자리 (2026-09-27 실측)
 *
 * `GET /modules` 의 `staining-1.consumables` 실물: `{reagents: null, tips_by_rack: null, tips_remaining: null,
 * reagents_by_well: null}` · `assembler-1`: `{reagents: null, tc_remaining: null}`. 칸은 있는데 값이 전부 `null`
 * (「탑재했으나 미상」 · module-service 계약 `consumables` 머리말)이라 지금은 아무것도 `used` 로 못 적는다.
 * 값이 오면 아래 순서로 잇는다 — **모듈 스냅샷을 인자로 더하고, 이 함수 안에서만** 푼다:
 *
 * 1. **팁 랙** — `consumables.tips_by_rack[]` `{labwareId, used_slots}`(신설 2026-09-25 · P4-E · v0.9.3 에서
 *    `labware_id` → `labwareId`). `labwareId` 가 `DeckRack.labwareId`(`tips_1000_a` · `tips_1000_b`)와 같은 줄의 `used_slots` 칸을
 *    `used` 로 적는다. ⚠ `used_slots` 는 «웰 이름»이 아니라 **1-기반 색인**이다(계약: 「랩웨어 `well_names`
 *    의 1-기반 색인」).
 *    ✅ **이었다(2026-10-01 · 히나 — 백엔드 코드로 확인).** 칸을 고르는 쪽이 L2 다: `ctcells-core`
 *    `app/services/staining_plan.py` 의 `TipPlan.next()` 가 레시피 `labware` 의 `tip_rack` 을 선언 순서로 돌며
 *    `enumerate(well_names, start=1)` 로 `(labware_id, well, slot)` 을 내고, 모듈은 받은 `slot` 을 그대로 `used_slots` 에
 *    적는다(`module-core/app/core/runtime.py` `tips_by_rack`). 같은 대응을 `app/services/move_insertion.py` 가 「E-P16 —
 *    `slot=N ⇔ well_names[N-1]`」로 적어 두었다. 실물 레시피의 팁 랙도 `well_names` 를 싣는다(fixture 는 그 모양 그대로).
 *    ⇒ **레시피 랩웨어의 `well_names` 로 푼다.** 🔵 **레시피가 없으면(도는 작업 없음) 랙 정의의 기본 차례로 푼다**(2026-10-26
 *    팀장 결정 · 히나 확인) — 서버가 차례를 짓는 곳(`recipe_v2/labware.py` `well_order()`)의 기본값이 정의 `ordering` 을 펼친 열
 *    차례이고(`pick_order: "row"` 만 줄 차례 · 실서버 레시피 29건 중 0건), 그림이 번들 사본으로 서는 것과 같은 정의다(`bundledSlotOrder`).
 *    레시피 정의가 있는데 `well_names` 가 없거나 범위 밖 색인이 섞이면 풀지 않고 종전대로 가득 + 「칸별 사용 기록 미수신」이다.
 *    ⚠ 「`ordering` 으로 짐작하지 않는다」(2026-10-01)는 이렇게 고쳐 읽는다 — 짐작이 아니라 서버 기본 차례의 출처다. 푸는 곳은 `supplySummary`
 *    (`SupplyItem.usedWells`) 이고 칸 맵은 아래 `deckOccupancy()` 가 정한다.
 *    `tips_remaining` 은 그 합과 같아야 한다는 게 계약이니 **수만 있고 칸이 없으면 칸을 지어내지 않는다.**
 * 2. **튜브 랙** — 튜브 «자리 점유»(꽂혔나 · 뺐나)를 말하는 경로는 **없다**. 가까운 것은
 *    `consumables.reagents_by_well[]` `{labwareId, wellName, volume}`(v0.9.3) 인데 이것은 «잔량»이지 튜브 유무가 아니다
 *    (0 µL 도 튜브는 꽂혀 있다). eric 의 「빼거나 하도록 할 예정」은 이 칸으로 안 풀린다 — 백엔드 요청 건이다.
 *    레시피 `reagents[].volumeByWell` 은 «담을 계획»이라 상태 출처가 아니다(시약 표시에만 쓴다 · 아래).
 * 3. **TC 적재소** — `consumables.tc_remaining`(수)뿐이다. 어느 칸이 비었는지는 없다(2026-10-30 히나 실측 — 레시피 TC 집기
 *    `source` 는 `{location_kind: "tc_stock"}` 뿐이고 `slot` 이 없다 · 모듈 장부도 TC 는 수만 센다 · 팁의 `tips_by_rack` 같은 칸 기록 없음).
 *    🔵 **사이 값은 정해진 차례로 앞에서부터 비운다**(아래 `tcUsedCells`) — 🔴 칸 순서는 서버 · 모듈이 보내지 않아 레시피 단계 이름
 *    차례를 따른 표시다(eric 2026-10-29 결정 · 백엔드 요청 BR 대기). 칸을 실제로 기록하는 경로가 생기면 그 값으로 바꾼다.
 *
 * ## 🔴 「수로 칸을 고르지 마라」의 예외 둘 — 0 과 가득 (eric 확정 2026-09-28)
 *
 * 장비가 보고한 «칸 수»(팁 · TC · `count`)가 **0 이면 전부 비움, 전체와 같으면 전부 가득**이다 — 어느 칸인지
 * 고를 필요가 없는 두 경우다. 그 **사이 값은 칸을 고르지 않는다** — 단 TC 적재소만 위 3번의 정해진 차례로 비운다
 * (eric 2026-10-29). 수가 기본값(미수신)이면 이 예외를 타지 않는다 — 부르는 쪽이 `live` 수만 넘긴다.
 * ⚠ 튜브 랙의 부피(µL)는 여기 오지 않는다 — 0 µL 도 튜브는 꽂혀 있다(위 2번).
 * 4. **분주 기록** — 구독 `events/stream` 의 명령 결과(`mount_tip` · `draw` 성공)로 칸을 지우는 길은
 *    계약(`DM-SSOT-10` ⓔ·ⓕ)상 **모듈이 한 뒤 위 두 칸으로 보고**하는 것이다. 화면이 이벤트로 칸을 셈하지
 *    않는다 — 프론트는 상태를 추론하지 않는다.
 */

/** 칸 상태를 정하는 데 쓰는 입력 — 랙의 정체 + (있으면) 장비가 보고한 칸 수(위 「예외 둘」) */
export interface OccupancySource {
  kind: DeckRackKind
  /** 레시피 `payload.labware[].id` — `DeckRack.labwareId` */
  labwareId: string
  /**
   * 장비가 보고한 남은 칸 수와 전체 칸 수(`SupplyItem` 의 `live` · `count` 값). 없으면(미수신 · 랙별 미수신)
   * 칸을 가득으로 둔다. 🔴 0 과 전체만 칸 맵을 바꾼다 — 사이 값은 칸을 고르지 않는다
   */
  count?: { remaining: number; total: number } | undefined
  /**
   * 🔴 **장비가 «칸으로» 보고한 쓴 칸**(팁 랙 `tips_by_rack[].used_slots` → 레시피 `well_names` 로 푼 웰 이름 · 위 1번).
   * 있으면 그 칸만 `used` 이고 나머지는 `full` 이다 — 수로 고른 칸이 아니라 장비가 적은 칸이다. 못 풀었으면 안 넘긴다.
   */
  usedWells?: readonly string[] | undefined
}

const ALL_FULL: RackOccupancy = Object.freeze({})
const ALL_USED: RackOccupancy = Object.freeze({ [REST_CELLS]: 'used' })

/**
 * 그 랙의 칸 상태 맵 — 보고된 수가 0 이면 전부 `used`, 장비가 칸으로 보고한 쓴 칸(`usedWells`)이 있으면 그 칸만 `used`,
 * 그 밖은 빈 맵(= 전부 `full`)
 */
export function deckOccupancy({ count, usedWells }: OccupancySource): RackOccupancy {
  if (count !== undefined && count.total > 0 && count.remaining <= 0) return ALL_USED
  if (usedWells !== undefined && usedWells.length > 0) {
    return Object.freeze(Object.fromEntries(usedWells.map((well) => [well, 'used' as const])))
  }
  return ALL_FULL
}

/**
 * **그 랙에 시약이 담긴 칸** — 도는 작업 레시피 `payload.reagents` 에서 `labware_id` 가 이 랙인 시약의
 * `volumeByWell` 키(= 그 시약이 놓인 웰 · `ReagentDef` 머리말)를 모은다. 값은 `display_color`(없으면 `null`).
 * 레시피가 없으면 `undefined` — 그림은 시약 테를 안 그린다.
 * ⚠ 이것은 «상태»가 아니라 **계획**이다 — 칸이 `used` 인지와 무관하게 얹힌다(빠진 튜브에는 그림이 안 그린다).
 * ⚠ 실물 레시피 17건 · 시약 전수에 `display_color` 가 **0건**이다(2026-09-27) — 지금은 전부 무채색 테다.
 */
export function deckReagentsAt(
  reagents: Readonly<Record<string, ReagentDef>> | undefined,
  labwareId: string,
): RackReagents | undefined {
  if (reagents === undefined) return undefined
  const out: Record<string, string | null> = {}
  for (const def of Object.values(reagents)) {
    // 접미 점 · 하이픈은 같은 자리다(`labwareKey` 머리말) — 옛 커밋 레시피는 점, 새 레시피 · 데크 표는 하이픈
    if (!sameLabware(def.labware_id, labwareId)) continue
    for (const well of Object.keys(def.volumeByWell ?? {})) {
      out[well] = def.display_color ?? null
    }
  }
  return out
}

/*
 * ─── 장비가 보고한 «수» — 소모품 요약(`supplySummary.ts`)이 읽는다 ───────────────────────────────
 *
 * 🔴 **칸 맵(`deckOccupancy`)과 수는 다른 물음이다.** 수는 칸 이름을 몰라도 센다(`used_slots.length`) —
 *    색인 → 웰 이름 풀이(위 머리말 1번)를 안 거친다. 그래서 칸 맵은 여전히 빈 맵이어도 수는 `live` 로 설 수 있다.
 * 🔴 **읽는 곳이 여기 하나다.** `consumables` 의 모양(키 부재 · `null` · 값)을 푸는 일을 요약 판 · 서랍이
 *    두 벌로 하지 않는다.
 */

/** 스냅샷 `consumables` 한 벌 — 키 이름은 module-service 계약 그대로 */
type ConsumablesBag = Readonly<Record<string, unknown>> | null | undefined

/**
 * **그 팁 랙에서 쓴 칸 수** — `tips_by_rack[]` 에서 `labwareId` 가 같은 줄의 `used_slots`(겹침 · 범위 밖은
 * 빼고 센다). 그 줄이 없으면 `undefined`(= 이 랙은 칸별로 보고되지 않았다).
 */
export function tipRackUsed(
  consumables: ConsumablesBag,
  labwareId: string,
  capacity: number,
): number | undefined {
  return tipRackUsedSlots(consumables, labwareId, capacity)?.length
}

/**
 * **그 팁 랙에서 쓴 칸의 색인**(1-기반 · 겹침 · 범위 밖은 뺀다 · 오름차순). 그 줄이 없으면 `undefined`.
 * 웰 이름으로 푸는 것은 부르는 쪽(`supplySummary`)이 레시피 `well_names` 로 한다(위 머리말 1번).
 */
export function tipRackUsedSlots(
  consumables: ConsumablesBag,
  labwareId: string,
  capacity: number,
): number[] | undefined {
  const racks = consumables?.['tips_by_rack']
  if (!Array.isArray(racks)) return undefined
  for (const entry of racks as unknown[]) {
    if (entry === null || typeof entry !== 'object') continue
    const row = entry as { labwareId?: unknown; used_slots?: unknown }
    if (row.labwareId !== labwareId || !Array.isArray(row.used_slots)) continue
    const slots = new Set(
      (row.used_slots as unknown[]).filter(
        (n): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= capacity,
      ),
    )
    return [...slots].sort((a, b) => a - b)
  }
  return undefined
}

/** 장비가 보고한 팁 총 잔량(`tips_remaining`) — 숫자가 아니면 `undefined`(키 부재 · `null` 둘 다 「모름」) */
export function tipsRemaining(consumables: ConsumablesBag): number | undefined {
  const value = consumables?.['tips_remaining']
  return typeof value === 'number' ? value : undefined
}

/**
 * TC 적재소 칸 이름 → 그림 칸 열쇠(`DeckRackFigure` 의 `'1'`…`'9'`). 거치대 정의(`ctcells/ctcells_tc_stock/1`)의 웰 좌표대로
 * A 줄이 위(+y 뒤) · 1 열이 왼쪽이다 — 그림 열쇠는 오른쪽 열 위→아래가 1·2·3, 가운데 4·5·6, 왼쪽 7·8·9 라 A1 = '7' · A3 = '1' · C3 = '3'.
 */
const TC_CELL_KEY: Readonly<Record<string, string>> = Object.freeze({
  A1: '7',
  A2: '4',
  A3: '1',
  B1: '8',
  B2: '5',
  B3: '2',
  C1: '9',
  C2: '6',
  C3: '3',
})

/**
 * TC 를 집는 기본 차례 — A1, A2, A3, B1 … C3(줄 우선). 🔴 서버 · 모듈이 칸 순서를 보내지 않아 레시피 단계 이름
 * 차례를 따른 표시다(eric 2026-10-29 결정 · 백엔드 요청 BR 대기).
 */
export const TC_PICK_ORDER: readonly string[] = Object.freeze([
  'A1',
  'A2',
  'A3',
  'B1',
  'B2',
  'B3',
  'C1',
  'C2',
  'C3',
])

/**
 * **TC 적재소에서 쓴 칸**(그림 칸 열쇠) — 남은 수 `remaining` 이 0 과 전체 사이일 때 차례 앞에서부터 `total − remaining` 칸.
 * 차례는 거치대 정의가 칸 차례(`wellOrder` · `well_names`)를 실어 왔고 그 이름을 전부 그림에 놓을 수 있으면 그것, 아니면
 * `TC_PICK_ORDER`. 0 · 가득 · 전체가 그림 칸 수와 다르면 `undefined` — 0 은 `deckOccupancy` 가 전부 비우고 가득은 전부 찬다.
 */
export function tcUsedCells(
  remaining: number,
  total: number,
  definition: Readonly<Record<string, unknown>> | undefined,
): string[] | undefined {
  if (total !== TC_PICK_ORDER.length || remaining <= 0 || remaining >= total) return undefined
  const declared = definition?.['wellOrder'] ?? definition?.['well_names']
  const order =
    Array.isArray(declared) &&
    declared.length === total &&
    declared.every((name) => typeof name === 'string' && name in TC_CELL_KEY)
      ? (declared as string[])
      : TC_PICK_ORDER
  return order.slice(0, total - remaining).map((name) => TC_CELL_KEY[name] ?? name)
}

/** TC 적재소 잔량(`tc_remaining`) — 숫자가 아니면 `undefined` */
export function tcRemaining(consumables: ConsumablesBag): number | undefined {
  const value = consumables?.['tc_remaining']
  return typeof value === 'number' ? value : undefined
}

/**
 * **그 웰의 지금 잔량(µL)** — `reagents_by_well[]` 에서 `labwareId`(접미 점 · 하이픈 같은 것 · `labwareKey`) · `wellName` 이 같은 줄의 `volume`.
 * 없으면 `undefined`(보고되지 않았다 — 「0 µL」이 아니다).
 */
export function wellVolume(
  consumables: ConsumablesBag,
  labwareId: string,
  well: string,
): number | undefined {
  const rows = consumables?.['reagents_by_well']
  if (!Array.isArray(rows)) return undefined
  for (const entry of rows as unknown[]) {
    if (entry === null || typeof entry !== 'object') continue
    const row = entry as { labwareId?: unknown; wellName?: unknown; volume?: unknown }
    if (
      typeof row.labwareId === 'string' &&
      sameLabware(row.labwareId, labwareId) &&
      row.wellName === well &&
      typeof row.volume === 'number'
    ) {
      return row.volume
    }
  }
  return undefined
}
